import esalesClient, { filtersToQs, checkMessageOrThrow } from '@/api/clients/esales'
import { AxiosInstance, AxiosResponse, Method } from 'axios'
import { INumericDictionary, IStringDictionary, IStringIndexed } from '@/modules/shared/types'
import { CustomerInvoice } from '@/modules/customers/models/customer/invoice'
import { InvoicesApi } from '@/modules/customers/api/invoices'
import { RequestParam } from '@/api/model'
import { File as FileModel } from '@/modules/shared/models/file'

// const loadAllParams = '&additional[]=customer&additional[]=billGroup&additional[]=invoices&additional[billGroup][]=contact'
// re approvalHoldNotes: load specific category of notes for use in the queue, map to 'notes' prop already handled by cust model
const loadAllParams = {
  additional: {
    0: 'customer',
    1: 'billGroup',
    2: 'invoices',
    customer: ['billingNotes'],
    billGroup: ['contact'],
    invoices: {
      0: 'lineItems',
      1: 'account',
      lineItems: ['endDate', 'startDate', 'usage', 'chargeDetails']
    }
  },
  map: {
    customer: {
      map: { billingNotes: 'notes' }
    }
  }
}

export class InvoiceQueueApi {
  public async loadAll (filters?: RequestParam): Promise<{ total: number, items: Array<CustomerInvoice> }> {
    try {
      const url = 'billing-invoices'

      const response: AxiosResponse = await esalesClient.request({
        url: url,
        method: <Method> 'POST',
        headers: {
          'X-HTTP-Method-Override': 'GET'
        },
        data: { filters: filters, ...loadAllParams }
      })

      if (response.status === 200 && response.data.invoices) {
        const list: Array<CustomerInvoice> = []

        Object.values(response.data.invoices as INumericDictionary<IStringIndexed>).forEach((i: IStringIndexed) => {
          list.push(new CustomerInvoice(i))
        })

        return { total: response.data.total, items: list }
      }

      throw new Error('Unexpected response format')
    } catch (err) {
      return checkMessageOrThrow(err)
    }
  }

  public async performAction (invoice: CustomerInvoice, doAction: string, data ?: IStringIndexed): Promise<{ invoice: CustomerInvoice, message?: string }> {
    try {
      const url = 'billing-invoices/' + invoice.id

      if (!data) {
        data = {}
      }

      data.do = doAction
      data.snapshotAt = invoice.updatedAt

      const response: AxiosResponse = await esalesClient.put(url, { ...data, ...loadAllParams })

      if (response.status === 205) {
        return {
          invoice: new CustomerInvoice(await InvoicesApi.load(invoice.customerId, invoice.id, undefined, loadAllParams.additional)),
          message: 'Invoice was updated, please review and retry your action as appropriate'
        }
      } else if (response.status === 200 && response.data.invoice) {
        return {
          invoice: new CustomerInvoice(response.data.invoice)
        }
      }

      throw new Error('Unexpected response format')
    } catch (err) {
      return checkMessageOrThrow(err)
    }
  }

  public async sendInvoices (invoices: Array<CustomerInvoice>): Promise<{ statusUpdates: IStringDictionary<string>, downloadFile?: FileModel, errorMessage?: string }> {
    try {
      const url = 'billing-invoices'
      const foo: Array<number> = []

      const data = {
        invoiceIds: invoices.reduce((c : Array<number>, i: CustomerInvoice) => { c.push(i.id); return c }, [] as Array<number>)
      }

      const response: AxiosResponse = await esalesClient.post(url, data)

      if (response.status === 200) {
        const resp: { statusUpdates: IStringDictionary<string>, downloadFile?: FileModel, errorMessage?: string } = {
          statusUpdates: response.data.statusUpdates
        }

        if (response.data.downloadDocument) {
          resp.downloadFile = new FileModel({ contents: response.data.downloadDocument })
        }

        if (response.data.error) {
          resp.errorMessage = response.data.error
        }

        return resp
      }

      throw new Error('Unexpected response format')
    } catch (err) {
      return checkMessageOrThrow(err)
    }
  }
}

export const InvoiceQueuesApi = new InvoiceQueueApi()
